// useSignaling — SignalingClient 生命周期管理
// 连接房间后通过 deviceStore 暴露 peers

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ClientInfo, ClientInfoWithoutId } from '@kuaichuan/shared';
import { SignalingClient } from '../lib/signaling/client';
import type { SignalingClientHandlers } from '../lib/signaling/client';
import { useDeviceStore } from '../store/deviceStore';
import { useSettingsStore } from '../store/settingsStore';
import { fetchConfig } from '../lib/api';

export interface JoinRoomOptions {
  /** 收到对端 OFFER（接收方触发，需调用 webrtc.receiveFrom） */
  onOffer?: (peer: ClientInfo, sessionId: string, sdp: string) => void;
  /** 收到对端 ANSWER（发送方触发，需调用 webrtc.receiveAnswer） */
  onAnswer?: (peer: ClientInfo, sessionId: string, sdp: string) => void;
}

export interface UseSignalingResult {
  client: SignalingClient | null;
  connected: boolean;
  connecting: boolean;
  error: string | null;
  joinRoom: (roomId: string, opts?: JoinRoomOptions) => Promise<void>;
  leaveRoom: () => void;
}

export function useSignaling(): UseSignalingResult {
  const [client, setClient] = useState<SignalingClient | null>(null);
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const clientRef = useRef<SignalingClient | null>(null);

  const { alias, deviceToken } = useSettingsStore();
  const { setSelf, setPeers, addPeer, removePeer, updatePeer, clear } = useDeviceStore();

  // 设备类型识别
  const detectDeviceType = (): ClientInfo['deviceType'] => {
    if (typeof window === 'undefined') return 'WEB';
    const ua = navigator.userAgent;
    const isMobile = /Mobile|Android|iPhone|iPad/i.test(ua);
    if (isMobile) return 'MOBILE';
    return 'DESKTOP';
  };

  const detectDeviceModel = (): string => {
    if (typeof navigator === 'undefined') return 'Unknown';
    const ua = navigator.userAgent;
    if (/Windows NT/.test(ua)) return 'Windows';
    if (/Macintosh/.test(ua)) return 'macOS';
    if (/Linux/.test(ua)) return 'Linux';
    if (/Android/.test(ua)) return 'Android';
    if (/iPhone|iPad/.test(ua)) return 'iOS';
    return navigator.platform || 'Unknown';
  };

  const buildInfo = (): ClientInfoWithoutId => ({
    alias,
    version: '2.1',
    deviceModel: detectDeviceModel(),
    deviceType: detectDeviceType(),
    token: deviceToken,
  });

  const joinRoom = useCallback(
    async (roomId: string, opts?: JoinRoomOptions) => {
      if (clientRef.current) {
        clientRef.current.disconnect();
        clientRef.current = null;
      }
      setConnecting(true);
      setError(null);
      clear();

      const handlers: SignalingClientHandlers = {
        onHello: (self, peers) => {
          setSelf(self);
          setPeers(peers);
        },
        onJoin: (peer) => addPeer(peer),
        onLeft: (peerId) => removePeer(peerId),
        onUpdate: (peer) => updatePeer(peer),
        onOffer: opts?.onOffer,
        onAnswer: opts?.onAnswer,
        onError: (code) => {
          setError(`服务器错误 (${code})`);
          if (code === 410) {
            setError('房间已过期或不存在');
          } else if (code === 429) {
            setError('请求过于频繁');
          }
        },
        onStateChange: (state) => {
          setConnected(state === 'open');
          setConnecting(state === 'connecting');
        },
        onClose: () => {
          setConnected(false);
        },
      };
      const c = new SignalingClient(handlers);
      clientRef.current = c;
      setClient(c);

      try {
        await c.connect(roomId, buildInfo());
      } catch (err) {
        setError((err as Error).message);
        setConnecting(false);
      }
    },
    [alias, deviceToken, setSelf, setPeers, addPeer, removePeer, updatePeer, clear],
  );

  const leaveRoom = useCallback(() => {
    clientRef.current?.disconnect();
    clientRef.current = null;
    setClient(null);
    setConnected(false);
    setConnecting(false);
    clear();
  }, [clear]);

  // 卸载时清理
  useEffect(() => {
    return () => {
      clientRef.current?.disconnect();
    };
  }, []);

  return { client, connected, connecting, error, joinRoom, leaveRoom };
}

// 拉取配置中的 STUN 服务器（缓存到模块级）
let cachedStunServers: string[] | null = null;
export async function getStunServers(): Promise<string[]> {
  if (cachedStunServers) return cachedStunServers;
  try {
    const cfg = await fetchConfig();
    cachedStunServers = cfg.stunServers.length > 0
      ? cfg.stunServers
      : ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'];
    return cachedStunServers;
  } catch {
    cachedStunServers = ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'];
    return cachedStunServers;
  }
}
